import { ActionButton } from "@via/components/action-button/action-button.tsx";
import Drawer from "@via/components/drawer.tsx";
import { PageHeading } from "@via/components/page-heading.tsx";
import { DrawerSize } from "@via/components/table/table.tsx";
import { HorizontalTabs } from "@via/components/tabs.tsx";
import CreateAppForm from "@via/pages/apps/create-app-form.tsx";
import {
  APPS_TABS,
  BUTTONS,
  PAGE_NAMES,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { type JSX, useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useUserConfigurations } from "@via/api/configuration";

export default function Apps(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const canModifyApp = user?.featureFlags?.FF_MODIFY_APPS;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [open, setDrawerOpen] = useState(false);
  // The cluster launches tab exists only while HPC apps are on (agent-9-1).
  const { data: configurations } = useUserConfigurations();
  const tabs = configurations?.HPC_APPS_ENABLED
    ? [...APPS_TABS, { name: "Cluster launches", href: "cluster-launches" }]
    : APPS_TABS;

  useEffect(() => {
    const newApp = searchParams.get("new");
    if (newApp) {
      setDrawerOpen(true);
    }
  }, [searchParams]);

  // Back to Available Apps when the create drawer closes, or when the URL asks for the drawer
  // (`?new`). Never on a plain load of a tab: a direct link to /apps/manage-status or
  // /apps/cluster-launches must stay where it points. A bare /apps is routes.tsx's index
  // <Navigate> already.
  const wasOpen = useRef(false);
  useEffect(() => {
    const closing = wasOpen.current && !open;
    wasOpen.current = open;
    if (!open && (closing || searchParams.has("new"))) {
      searchParams.delete("new");
      navigate({
        pathname: "/apps/available-apps",
        search: searchParams.toString(),
      });
    }
  }, [open]);

  return (
    <main className="via-content-wrapper">
      <div className="via-page-heading-wrapper">
        <PageHeading title={PAGE_NAMES.APPS} />
        {isAdmin || canModifyApp ? (
          <ActionButton
            button={BUTTONS.CREATE_APP}
            primary={true}
            onClick={() => {
              setDrawerOpen(true);
            }}
          />
        ) : null}
      </div>
      {isAdmin || canModifyApp ? (
        <Drawer
          id="apps-create"
          open={open}
          setOpen={setDrawerOpen}
          size={DrawerSize.Medium}>
          <CreateAppForm setOpen={setDrawerOpen} />
        </Drawer>
      ) : null}
      <HorizontalTabs buttonIdPrefix="apps" options={tabs} />
      <Outlet />
    </main>
  );
}
